/**
 * Loader de l'aperçu anonyme V2 d'un exercice (route `/demo/exercise/$id`).
 *
 * S'appuie sur la RPC `get_exercise_v2_demo` (migration 0099) — anon-callable,
 * security definer — qui tronque côté serveur aux 3 premières QUESTIONS
 * (blocs `statement_question` avec une bonne réponse), pas aux 3 premières
 * étapes. Les blocs `statement` intermédiaires ne consomment pas de quota.
 *
 * Renvoie `null` si l'exercice n'existe pas / n'est pas publié / n'est pas V2,
 * pour permettre au composant route de basculer sur le loader V1.
 */

import { supabase } from '@/lib/supabase';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';

/** Nombre de questions exposées par l'aperçu anonyme V2. */
export const DEMO_EXERCISE_V2_QUESTION_LIMIT = 3;

export interface DemoExerciseV2Meta {
  id: string;
  chapter_id: string | null;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  estimated_minutes: number | null;
  illustration_url: string | null;
  content_version: 'v2';
}

export interface DemoExerciseV2 {
  exercise: DemoExerciseV2Meta;
  blocks: ExerciseBlock[];
  subject: string | null;
  level: string | null;
  chapter_label: string;
  total_questions: number;
  demo_questions: number;
}

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: unknown }>;

/** Bonne difficulté ⇒ telle quelle, sinon `null` (défaut côté player). */
function asDifficulty(raw: unknown): 'easy' | 'medium' | 'hard' {
  return raw === 'easy' || raw === 'medium' || raw === 'hard' ? raw : 'medium';
}

/**
 * Convertit le payload brut de la RPC en `DemoExerciseV2`. Pur (testable) :
 * aucune dépendance Supabase. Renvoie `null` si le payload est inexploitable
 * (exercice absent côté SQL, aucun bloc, méta manquante).
 */
export function mapExerciseDemoV2Payload(raw: unknown): DemoExerciseV2 | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const ex = r.exercise as Record<string, unknown> | undefined;
  if (!ex || typeof ex.id !== 'string' || typeof ex.title !== 'string') return null;
  const blocks = Array.isArray(r.blocks) ? (r.blocks as ExerciseBlock[]) : [];
  if (blocks.length === 0) return null;
  return {
    exercise: {
      id: ex.id,
      chapter_id: typeof ex.chapter_id === 'string' ? ex.chapter_id : null,
      title: ex.title,
      difficulty: asDifficulty(ex.difficulty),
      estimated_minutes: typeof ex.estimated_minutes === 'number' ? ex.estimated_minutes : null,
      illustration_url: typeof ex.illustration_url === 'string' ? ex.illustration_url : null,
      content_version: 'v2',
    },
    blocks,
    subject: typeof r.subject === 'string' ? r.subject : null,
    level: typeof r.level === 'string' ? r.level : null,
    chapter_label: typeof r.chapter_label === 'string' ? r.chapter_label : '',
    total_questions: typeof r.total_questions === 'number' ? r.total_questions : 0,
    demo_questions: typeof r.demo_questions === 'number' ? r.demo_questions : 0,
  };
}

/**
 * Charge l'aperçu V2 d'un exercice. Renvoie `null` si l'exercice n'est pas V2
 * (le composant route fait alors un fallback vers `loadExerciseDemo` V1).
 * Lève sur erreur réseau / RPC.
 */
export async function loadExerciseDemoV2(exerciseId: string): Promise<DemoExerciseV2 | null> {
  const { data, error } = await (supabase.rpc as unknown as UntypedRpc)('get_exercise_v2_demo', {
    p_exercise_id: exerciseId,
  });
  if (error) throw error;
  if (data == null) return null;
  return mapExerciseDemoV2Payload(data);
}
